import React, { HTMLAttributes } from "react";

import useArticleSection from "@/components/business/FillInTableContainer/hooks/useArticleSection";
import { ArticleSection } from "@/types/articleEntryData.types";
import RequiredMark from "@atoms/RequiredMark/RequiredMark";
import { useCheckClassificationPreclinical } from "@FillInTableContainer/components/FillInTable/components/TableCreator/components/ClinicalTrialsTableCreator/hooks/useCheckClassificationPreclinical";
import { GroupTableKeyType } from "@FillInTableContainer/shared/types/tableCreator.types";
import { isRequiredGroupShell } from "@FillInTableContainer/shared/utils/getOptionsFromData";
import { getGroupShellTitle } from "@FillInTableContainer/shared/utils/getTextFromData";
import { padStart } from "@utils/padStart/padStart";

import * as S from "./TableCreatorGroupShellTitle.style";
import TableCreatorDragButton from "../TableCreatorDragButton/TableCreatorDragButton";

interface Props extends HTMLAttributes<HTMLDivElement> {
  articleSectionKey?: ArticleSection;
  groupShellKey: string | null;
  groupShellCount: number;
  groupShellIndex: number;
  customGroupShellTitle?: string;
  isAlertGroupShell?: boolean;
  onClickHandler?: () => void;
  changeFocusStatusForDragging?: () => void;
  changeFocusStatusForDragEnd?: () => void;
  handleDragStart?: (
    event: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => void;
  handleDragEnd?: (
    groupTableKey: GroupTableKeyType,
    tableKey?: string | null,
  ) => void;
  handleDragOn?: () => void;
  isDraggableGroupShell?: boolean;
  isDragging?: boolean;
  groupTableKey?: GroupTableKeyType;
  titleWrapperColor?: string;
}

const IS_DRAGGED_GROUP_SHELL = ["Rights Scope Row", "Main Financial Terms"];

const TableCreatorGroupShellTitle = ({
  articleSectionKey,
  groupTableKey,
  groupShellKey,
  groupShellCount,
  groupShellIndex,
  customGroupShellTitle,
  isAlertGroupShell = false,
  isDraggableGroupShell = false,
  isDragging = false,
  titleWrapperColor = "#fff",
  onClickHandler,
  changeFocusStatusForDragEnd,
  changeFocusStatusForDragging,
  handleDragEnd,
  handleDragOn,
  handleDragStart,
  ...rest
}: Props) => {
  const { isPreclinical } = useCheckClassificationPreclinical();

  const requiredGroupShellKey = isPreclinical ? null : groupShellKey;
  const articleSectionByClassification = useArticleSection();
  const articleSection = articleSectionKey || articleSectionByClassification;
  const isRequired = isRequiredGroupShell(
    articleSection,
    requiredGroupShellKey,
  );
  const currentGroupShellTitle =
    customGroupShellTitle || getGroupShellTitle(articleSection, groupShellKey);
  const groupShellTitle = () => {
    if (groupShellCount > 1)
      return `${currentGroupShellTitle} ${padStart(groupShellIndex + 1)}`;
    return currentGroupShellTitle;
  };

  return (
    <S.GroupShellTitle
      isAlertGroupShell={isAlertGroupShell}
      isDragging={isDragging}
      $titleWrapperColor={titleWrapperColor}
      onClick={onClickHandler}
      onDragStart={(e) => {
        if (handleDragStart && changeFocusStatusForDragging) {
          handleDragStart(e, groupShellIndex);
          changeFocusStatusForDragging();
        }
      }}
      onDragEnd={() => {
        if (handleDragEnd && changeFocusStatusForDragEnd && groupTableKey) {
          handleDragEnd(groupTableKey);
          changeFocusStatusForDragEnd();
        }
      }}
      onDrag={() => handleDragOn && handleDragOn()}
      draggable={isDraggableGroupShell}
      {...rest}
    >
      <S.TitleTextWrapper>
        {isRequired && <RequiredMark text="*" />}
        {groupShellTitle()}
      </S.TitleTextWrapper>

      <S.RightButtonsWrapper>
        {groupShellCount > 1 &&
          IS_DRAGGED_GROUP_SHELL.includes(groupShellKey || "") && (
            <TableCreatorDragButton
              isDragging={isDragging}
              dragContainer="groupShell"
              onClick={(e) => {
                e.stopPropagation();
                if (changeFocusStatusForDragging) {
                  changeFocusStatusForDragging();
                }
              }}
            />
          )}
      </S.RightButtonsWrapper>
    </S.GroupShellTitle>
  );
};

export default TableCreatorGroupShellTitle;
